import type { BenefitDetailDto, BenefitGrantDto, BenefitGrantReview, BenefitWorkflowResponse } from "./benefit-types"
import { deliveryReadbackDisplay } from "./benefit-workflow"

export type GrantCapabilityKind = "review" | "certify" | "corrections" | "invalidate" | "certification-correction"
export type GrantTaskName = GrantCapabilityKind | "details" | "history" | "payments" | "returned-recovery" | "replacement"
export type GrantEditContext = {
  benefitId: string
  concurrencyToken: string
  programId: number
  programYear: number
  revision: number
  generationId: string | null
  predecessorGenerationId: string | null
  reviewVersion: number
}

export function usableGrantAuthority(workflow: BenefitWorkflowResponse | null): workflow is BenefitWorkflowResponse {
  return workflow?.authority === "V2" && workflow.display?.authority === "V2" && workflow.display.readUnavailable === false && Array.isArray(workflow.grantReviews)
}

export function actionableGrantReview(review: BenefitGrantReview | null | undefined): review is BenefitGrantReview {
  return !!review && ["Created", "Proofed", "FixRequested", "Certified", "Invalidated"].includes(review.state) &&
    Number.isSafeInteger(review.version) && review.version > 0 && Array.isArray(review.capabilities) &&
    (review.generationId === null || (typeof review.generationId === "string" && review.generationId.length > 0)) && !deliveryReadbackDisplay(review.delivery).unavailable
}

export function grantCapability(review: BenefitGrantReview | null, kind: GrantCapabilityKind) {
  if (!actionableGrantReview(review)) return undefined
  const target = kind === "review" ? "Proofed" : kind === "certify" || kind === "certification-correction" ? "Certified" : kind === "invalidate" ? "Invalidated" : "FixRequested"
  const matches = review.capabilities.filter(capability => capability?.target === target &&
    (capability.correction === true) === (kind === "certification-correction"))
  const capability = matches.length === 1 ? matches[0] : undefined
  return capability?.enabled === true && typeof capability.requiresReason === "boolean" &&
    typeof capability.requiresExplanation === "boolean" &&
    (capability.correction === undefined || typeof capability.correction === "boolean") ? capability : undefined
}

export function grantTaskAllowed(review: BenefitGrantReview | null, task: GrantTaskName) {
  if (!actionableGrantReview(review)) return false
  if (task === "returned-recovery") return review.returnedRecovery?.enabled === true
  if (task === "replacement") return review.state === "Invalidated" && review.replacement?.enabled === true && !!review.replacement.predecessorGenerationId
  if (task === "details" || task === "history" || task === "payments") return false
  return !!grantCapability(review, task) || (task === "review" && !!grantCapability(review, "corrections"))
}

export function captureGrantContext(benefit: BenefitDetailDto, grant: BenefitGrantDto, review: BenefitGrantReview): GrantEditContext | null {
  if (!benefit.concurrencyToken || !actionableGrantReview(review)) return null
  return {
    benefitId: benefit.id, concurrencyToken: benefit.concurrencyToken,
    programId: grant.benefitProgramInstance.program, programYear: grant.benefitProgramInstance.year,
    revision: grant.benefitProgramInstance.revision, generationId: review.generationId,
    predecessorGenerationId: review.replacement?.predecessorGenerationId ?? null, reviewVersion: review.version,
  }
}

export function currentGrantContext(context: GrantEditContext | null, benefit: BenefitDetailDto, workflow: BenefitWorkflowResponse | null) {
  if (!context || !usableGrantAuthority(workflow) || benefit.id !== context.benefitId || benefit.concurrencyToken !== context.concurrencyToken) return null
  const grants = benefit.benefitGrants.filter(grant => grant.benefitProgramInstance.program === context.programId && grant.benefitProgramInstance.year === context.programYear)
  const reviews = workflow.grantReviews.filter(review => review.programId === context.programId && review.programYear === context.programYear)
  const review = reviews.length === 1 ? reviews[0] : null
  if (grants.length !== 1 || grants[0]!.benefitProgramInstance.revision !== context.revision || !actionableGrantReview(review) ||
    review.generationId !== context.generationId || review.version !== context.reviewVersion ||
    (review.replacement?.predecessorGenerationId ?? null) !== context.predecessorGenerationId) return null
  return { context, review }
}

/** Only the supporting facts displayed by a grant task, not a second application cache. */
export function captureGrantTaskBasis(benefit: BenefitDetailDto, grant: BenefitGrantDto, workflow: BenefitWorkflowResponse | null) {
  return structuredClone({
    reasons: workflow?.reasons.filter(reason => reason.group === "GrantReview") ?? [],
    eligibility: benefit.benefitEligibility.filter(item => item.benefitProgramInstance.program === grant.benefitProgramInstance.program &&
      item.benefitProgramInstance.year === grant.benefitProgramInstance.year),
    householdRecords: benefit.personCollection.length,
    monthlyAdjustedIncome: benefit.monthlyAdjustedIncome,
    eligibilityRecords: benefit.benefitEligibility.length,
    calculationAvailable: !!benefit.calculationDetails,
  })
}
export type GrantTaskBasis = ReturnType<typeof captureGrantTaskBasis>

/** Versions alone do not cover unversioned payments, delivery or policy/catalog changes.
 * Equality only disables a stale form; it never grants authority or creates a write token.
 */
export function unchangedGrantTask(grant: BenefitGrantDto, review: BenefitGrantReview | null, basis: GrantTaskBasis | null,
  benefit: BenefitDetailDto, workflow: BenefitWorkflowResponse | null) {
  if (!basis || !review || !usableGrantAuthority(workflow)) return false
  const grants = benefit.benefitGrants.filter(item => item.benefitProgramInstance.program === grant.benefitProgramInstance.program &&
    item.benefitProgramInstance.year === grant.benefitProgramInstance.year)
  const reviews = workflow.grantReviews.filter(item => item.programId === review.programId && item.programYear === review.programYear)
  return grants.length === 1 && reviews.length === 1 && JSON.stringify(grants[0]) === JSON.stringify(grant) &&
    JSON.stringify(reviews[0]) === JSON.stringify(review) &&
    JSON.stringify(captureGrantTaskBasis(benefit, grant, workflow)) === JSON.stringify(basis)
}

export function validRequiredReason(value: string, reasons: BenefitWorkflowResponse["reasons"]): number | null {
  if (!/^\d+$/.test(value)) return null
  const code = Number(value)
  if (!Number.isSafeInteger(code) || code <= 0) return null
  return reasons.length === 0 || reasons.filter(reason => reason.code === code).length === 1 ? code : null
}

export function validGrantDate(value: string | undefined): boolean {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

export function validReplacement(amount: number | undefined, revision: number | undefined, date: string | undefined): boolean {
  return amount != null && Number.isFinite(amount) && amount > 0 &&
    Math.abs(amount * 100 - Math.round(amount * 100)) < 1e-7 &&
    revision != null && Number.isSafeInteger(revision) && revision >= 0 && validGrantDate(date)
}

export function paymentPledgeLabel(workflow: BenefitWorkflowResponse | null, benefitId: string, grant: BenefitGrantDto, vendorId: number): string {
  if (!usableGrantAuthority(workflow) || !Array.isArray(workflow.pledgeGenerations)) return "Unavailable"
  const matches = workflow.pledgeGenerations.filter(pledge => pledge.isCurrent && pledge.benefitId === benefitId &&
    pledge.programId === grant.benefitProgramInstance.program && pledge.programYear === grant.benefitProgramInstance.year && pledge.vendorId === vendorId)
  return matches.length === 1 ? matches[0]!.status : matches.length === 0 ? "No current pledge" : "Unavailable"
}

/** Reuse a key only for the identical request after an uncertain/lost response. */
export function grantAttempt(previous: { signature: string; key: string } | null, path: string, body: object, newKey: () => string) {
  const signature = JSON.stringify([path, body])
  return previous?.signature === signature ? previous : { signature, key: newKey() }
}
